import { SignInMode } from '../db-entries/index.js';
import { MfaFactor, MfaPolicy, OrganizationRequiredMfaPolicy, SignInIdentifier, } from '../foundations/index.js';
import { adminTenantId, defaultTenantId } from './tenant.js';
/**
 * ULDZA brand palette. When `branding.logoUrl` is not set, the sign-in experience falls back to
 * the bundled ULDZA logo asset (`packages/experience/src/assets/brand/`), so a fresh deployment
 * shows ULDZA branding without any database or Admin Console configuration.
 *
 * NOTE [ULDZA fork]: upstream defaults were primaryColor `#6139F6`, light-mode only, and
 * `logoUrl: 'https://logto.io/logo.svg'`. See `README-BRANDING.md` for how to revert or override.
 */
export const defaultPrimaryColor = '#0284C7';
/** ULDZA dark-mode primary color (dark blue). */
export const defaultDarkPrimaryColor = '#0369A1';
export const createDefaultSignInExperience = (forTenantId, isCloud) => Object.freeze({
    tenantId: forTenantId,
    id: 'default',
    color: {
        primaryColor: defaultPrimaryColor,
        isDarkModeEnabled: true,
        darkPrimaryColor: defaultDarkPrimaryColor,
    },
    // Empty branding: the experience UI renders its bundled ULDZA logo/favicon as fallback.
    // Set `logoUrl`/`favicon` (Admin Console → Sign-in experience, or the Management API) to
    // point at custom assets served over an absolute URL.
    branding: {},
    hideLogtoBranding: false,
    languageInfo: {
        autoDetect: true,
        fallbackLanguage: 'en',
    },
    termsOfUseUrl: null,
    privacyPolicyUrl: null,
    signUp: {
        identifiers: [isCloud ? SignInIdentifier.Email : SignInIdentifier.Username],
        password: true,
        verify: isCloud,
    },
    signIn: {
        methods: [
            {
                identifier: isCloud ? SignInIdentifier.Email : SignInIdentifier.Username,
                password: true,
                verificationCode: false,
                isPasswordPrimary: true,
            },
        ],
    },
    socialSignInConnectorTargets: [],
    signInMode: SignInMode.SignInAndRegister,
    customCss: null,
    customContent: {},
    customUiAssets: null,
    signUpProfileFields: null,
    passwordPolicy: {},
    mfa: {
        factors: [],
        policy: MfaPolicy.UserControlled,
    },
});
/** @deprecated Use `createDefaultSignInExperience()` instead. */
export const defaultSignInExperience = createDefaultSignInExperience(defaultTenantId, false);
export const createAdminTenantSignInExperience = (options = {}) => Object.freeze({
    ...defaultSignInExperience,
    tenantId: adminTenantId,
    color: {
        ...defaultSignInExperience.color,
        isDarkModeEnabled: true,
    },
    signInMode: SignInMode.Register,
    // Empty branding: same bundled ULDZA fallback as the default tenant (see above).
    branding: {},
    passwordPolicy: options.disablePwnedPasswordCheck
        ? {
            ...defaultSignInExperience.passwordPolicy,
            rejects: {
                ...defaultSignInExperience.passwordPolicy?.rejects,
                pwned: false,
            },
        }
        : defaultSignInExperience.passwordPolicy,
    mfa: {
        factors: [MfaFactor.TOTP, MfaFactor.WebAuthn, MfaFactor.BackupCode],
        policy: MfaPolicy.NoPrompt,
        organizationRequiredMfaPolicy: OrganizationRequiredMfaPolicy.Mandatory,
    },
});
